import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { formatMetric } from '../panelConfig';
import PriceChangeImpact from './PriceChangeImpact';

const fmtDay = (iso) => {
  const p = (iso || '').split('-');
  return p.length === 3 ? `${Number(p[2])}/${Number(p[1])}` : iso;
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload ?? {};
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-gray-900">{fmtDay(label)}</div>
      {p.price != null && (
        <div className="text-gray-700">Precio: <b>{formatMetric(p.price, 'currency')}</b></div>
      )}
      <div className="text-gray-700">Vendidas: <b>{p.units || 0}</b>{p.revenue ? ` · ${formatMetric(p.revenue, 'currency')}` : ''}</div>
    </div>
  );
}

/**
 * Sección de precios del Catálogo: arriba el impacto de cambios de precio
 * (caídas + ganadores, demostrativo), abajo el buscador + gráfico detalle de un
 * producto puntual. Click en una fila del impacto abre su detalle acá mismo.
 */
export default function PriceSection({ fetcher, currency }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // { sku, product_name }
  const [series, setSeries] = useState(null);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    const q = term.trim();
    if (selected || q.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      try {
        const res = await fetcher({ q });
        if (id === reqId.current) setResults(res?.results ?? []);
      } catch (_e) {
        if (id === reqId.current) setResults([]);
      } finally {
        if (id === reqId.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [term, selected, fetcher]);

  const pick = async (sku, productName) => {
    setSelected({ sku, product_name: productName });
    setResults([]);
    setTerm(productName || sku);
    setLoadingSeries(true);
    setSeries(null);
    try {
      setSeries(await fetcher({ sku }));
    } catch (_e) {
      setSeries(null);
    } finally {
      setLoadingSeries(false);
    }
  };

  const clear = () => {
    setSelected(null);
    setSeries(null);
    setTerm('');
    setResults([]);
  };

  const points = useMemo(() => series?.points ?? [], [series]);
  const hasData = points.some((p) => p.price != null || p.units);

  return (
    <div className="cat-panel">
      <div className="cat-ph">Precios y ventas{currency ? ` · ${currency}` : ''}</div>
      <div className="cat-h2">Impacto de cambios de precio</div>
      <p className="cat-sub">
        Productos que vendían y les cambiaste el precio — en rojo los que <b>cayeron</b> al subir,
        en verde los que <b>ganaron</b> al bajar. Tocá uno para ver su historial.
      </p>

      <div style={{ marginTop: 14 }}>
        <PriceChangeImpact fetcher={fetcher} onPick={pick} />
      </div>

      <div className="cat-h2" style={{ marginTop: 22, fontSize: 15 }}>Explorar un producto</div>
      <div className="cat-search" style={{ marginTop: 10 }}>
        <div className="box">
          <Search size={16} color="#5B5F7A" />
          <input
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              if (selected) setSelected(null);
            }}
            placeholder="Buscá por nombre o SKU…"
          />
          {(term || selected) && (
            <button type="button" onClick={clear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B5F7A' }}>
              <X size={16} />
            </button>
          )}
        </div>
        {!selected && results.length > 0 && (
          <div className="results">
            {results.map((r) => (
              <button key={r.sku} type="button" onClick={() => pick(r.sku, r.product_name)}>
                <span>{r.product_name || r.sku}</span>
                <span className="meta">{r.price != null ? formatMetric(r.price, 'currency') : '—'} · {r.units_sold} u.</span>
              </button>
            ))}
          </div>
        )}
        {!selected && searching && <p className="cat-hint">Buscando…</p>}
        {!selected && !searching && term.trim().length >= 2 && results.length === 0 && (
          <p className="cat-hint">Sin resultados para “{term.trim()}”.</p>
        )}
      </div>

      {selected && (
        <div style={{ marginTop: 14 }}>
          {loadingSeries ? (
            <div className="cat-empty sm">Cargando historial…</div>
          ) : !hasData ? (
            <div className="cat-empty sm">Todavía no hay historial de precio/ventas para este producto.</div>
          ) : (
            <>
              <div className="cat-ph">{series?.product_name || selected.sku} · línea = precio · barras = unidades/día</div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: '#5B5F7A' }} minTickGap={24} />
                  <YAxis yAxisId="price" tick={{ fontSize: 11, fill: '#5B5F7A' }} tickFormatter={(v) => formatMetric(v, 'currency')} width={72} />
                  <YAxis yAxisId="units" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} width={32} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar yAxisId="units" dataKey="units" name="Unidades" fill="#C0C9FF" radius={[2, 2, 0, 0]} />
                  <Line yAxisId="price" type="stepAfter" dataKey="price" name="Precio" stroke="#1B3DFB" strokeWidth={2} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      )}
    </div>
  );
}
